import React from 'react';
import { Button, ButtonProps, styled } from '@mui/material';
import { AutoAwesome } from '@mui/icons-material';
import {
  GRADIENT_PRIMARY,
  pulseAnimation,
  SECONDARY_TEXT_COLOR_HOVER,
} from 'components/AI/AIStyles';

const StyledAIButton = styled(Button)(({ theme, variant }) => ({
  position: 'relative',
  transition: 'all ease-in-out 350ms',
  ...(variant === 'contained' && {
    border: '0',
    background: GRADIENT_PRIMARY,
    color: theme.palette.common.white,
    '&:hover': {
      color: theme.palette.common.white,
      animation: `${pulseAnimation} 2s 1`,
      boxShadow: '0px 5px 20px rgba(0, 0, 0, 0.1)',
    },
  }),
  ...(variant === 'text' && {
    color: '#00afcc',
    '&:hover': {
      color: SECONDARY_TEXT_COLOR_HOVER,
      animation: `${pulseAnimation} 2s 1`,
    },
  }),
}));

export interface AIButtonProps extends Omit<ButtonProps, 'startIcon'> {
  /**
   * Text to display on the button
   */
  text?: string;
  /**
   * Whether to show the icon
   */
  showIcon?: boolean;
  /**
   * Function to call when the button is clicked
   */
  onAIAction?: () => void;
}

/**
 * AI Button component with a sparkling icon
 *
 * @param props - Component props
 * @returns React component
 */
export const AIButton: React.FC<AIButtonProps> = ({
  text = 'AI',
  showIcon = true,
  onAIAction,
  onClick,
  children,
  variant,
  ...rest
}) => {
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (onClick) onClick(event);
    if (onAIAction) onAIAction();
  };

  return (
    <StyledAIButton
      variant={variant}
      startIcon={showIcon ? <AutoAwesome /> : undefined}
      onClick={handleClick}
      {...rest}
    >
      {children || text}
    </StyledAIButton>
  );
};

export default AIButton;
